/**
 * @file lib/git/git-embedding-sdk.ts
 * @description Git対応のEmbeddingSDK
 * @author GFTD Co., Ltd.
 */

import { HuggingFaceTransformersEmbeddings } from '@langchain/community/embeddings/hf_transformers';
import { Document } from '@langchain/core/documents';
import { GitFileSystem, GitConfig } from './git-fs';
import { GitVectorStore, GitSearchResult } from './git-vector-store';

/**
 * Git対応Embedding設定インターフェース
 */
export interface GitEmbeddingConfig {
  /** Git設定 */
  git: GitConfig;
  /** モデル名 (デフォルト: 'Xenova/all-MiniLM-L6-v2') */
  modelName?: string;
  /** ベクトル次元数 */
  dimensions?: number;
}

/**
 * Git対応EmbeddingSDKクラス
 * HuggingFaceTransformersEmbeddingsとGitを使用してテキストをベクトル化し、
 * Gitリポジトリで管理
 */
export class GitEmbeddingSDK {
  private embeddings: HuggingFaceTransformersEmbeddings;
  private gitFs: GitFileSystem;
  private vectorStore: GitVectorStore;
  private config: GitEmbeddingConfig;
  private isInitialized = false;

  constructor(config: GitEmbeddingConfig) {
    this.config = config;

    // HuggingFaceTransformersEmbeddingsの初期化
    this.embeddings = new HuggingFaceTransformersEmbeddings({
      modelName: config.modelName || 'Xenova/all-MiniLM-L6-v2',
    });

    // GitFileSystemの初期化
    this.gitFs = new GitFileSystem(config.git);
    
    // GitVectorStoreの初期化
    this.vectorStore = new GitVectorStore(this.gitFs, config.dimensions || 384);
  }

  /**
   * SDKの初期化
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // GitVectorStoreの初期化（GitFileSystemも含む）
      await this.vectorStore.initialize();

      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize GitEmbeddingSDK:', error);
      throw error;
    }
  }

  /**
   * テキストをembeddingに変換
   * @param text テキスト
   * @returns embedding vector
   */
  async embedText(text: string): Promise<number[]> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const vectors = await this.embeddings.embedQuery(text);
      return vectors;
    } catch (error) {
      console.error('Failed to embed text:', error);
      throw error;
    }
  }

  /**
   * 複数のテキストをembeddingに変換
   * @param texts テキスト配列
   * @returns embedding vectors
   */
  async embedTexts(texts: string[]): Promise<number[][]> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const vectors = await this.embeddings.embedDocuments(texts);
      return vectors;
    } catch (error) {
      console.error('Failed to embed texts:', error);
      throw error;
    }
  }

  /**
   * ドキュメントをembeddingに変換してVectorStoreに追加
   * @param documents ドキュメント配列
   * @returns 追加されたドキュメントのID配列
   */
  async addDocuments(documents: Document[]): Promise<string[]> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const texts = documents.map(doc => doc.pageContent);
      const vectors = await this.embedTexts(texts);
      
      const ids: string[] = [];
      
      for (let i = 0; i < documents.length; i++) {
        const id = await this.vectorStore.add(vectors[i], documents[i]);
        ids.push(id);
      }

      return ids;
    } catch (error) {
      console.error('Failed to add documents:', error);
      throw error;
    }
  }

  /**
   * 類似検索
   * @param query 検索クエリ
   * @param k 返す結果数
   * @returns 類似ドキュメント配列
   */
  async search(query: string, k: number = 5): Promise<{ document: Document; score: number; id: string; commitSha?: string }[]> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const queryVector = await this.embedText(query);
      const results = await this.vectorStore.search(queryVector, k);
      
      return results.map((result: GitSearchResult) => ({
        document: result.document,
        score: result.score,
        id: result.id,
        commitSha: result.commitSha,
      }));
    } catch (error) {
      console.error('Failed to search:', error);
      throw error;
    }
  }

  /**
   * 新しいセッション（ブランチ）を作成
   * @param sessionName セッション名
   * @param baseBranch 基点となるブランチ（デフォルト: 現在のブランチ）
   */
  async createSession(sessionName: string, baseBranch?: string): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      // 現在のブランチを基点として新しいブランチを作成
      const currentBranch = baseBranch || this.vectorStore.getCurrentBranch();
      await this.vectorStore.switchBranch(sessionName, true);
      
      console.log(`Created new session: ${sessionName} from ${currentBranch}`);
    } catch (error) {
      console.error('Failed to create session:', error);
      throw error;
    }
  }

  /**
   * セッション（ブランチ）を切り替え
   * @param sessionName セッション名
   */
  async switchSession(sessionName: string): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      await this.vectorStore.switchBranch(sessionName, false);
      console.log(`Switched to session: ${sessionName}`);
    } catch (error) {
      console.error('Failed to switch session:', error);
      throw error;
    }
  }

  /**
   * 現在のセッション（ブランチ）を取得
   */
  getCurrentSession(): string {
    return this.vectorStore.getCurrentBranch();
  }

  /**
   * セッション（ブランチ）一覧を取得
   */
  async listSessions(): Promise<string[]> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    return this.vectorStore.listBranches();
  }

  /**
   * リモートにプッシュ
   */
  async push(): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      await this.vectorStore.push();
      console.log('Pushed to remote repository');
    } catch (error) {
      console.error('Failed to push:', error);
      throw error;
    }
  }

  /**
   * リモートからプル
   */
  async pull(): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      await this.vectorStore.pull();
      console.log('Pulled from remote repository');
    } catch (error) {
      console.error('Failed to pull:', error);
      throw error;
    }
  }

  /**
   * ファイルをGitリポジトリに保存
   * @param filePath ファイルパス
   * @param content ファイル内容
   * @param commitMessage コミットメッセージ
   */
  async saveFile(filePath: string, content: string, commitMessage?: string): Promise<string> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      await this.gitFs.writeFile(filePath, content);
      
      const message = commitMessage || `Save file: ${filePath}`;
      const commitSha = await this.gitFs.commit(message, [filePath]);
      
      return commitSha;
    } catch (error) {
      console.error('Failed to save file:', error);
      throw error;
    }
  }

  /**
   * ファイルをGitリポジトリから読み込み
   * @param filePath ファイルパス
   * @returns ファイル内容
   */
  async loadFile(filePath: string): Promise<string> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      return await this.gitFs.readFile(filePath);
    } catch (error) {
      console.error('Failed to load file:', error);
      throw error;
    }
  }

  /**
   * ファイルが存在するかチェック
   * @param filePath ファイルパス
   */
  async fileExists(filePath: string): Promise<boolean> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    return this.gitFs.exists(filePath);
  }

  /**
   * ディレクトリの内容を一覧取得
   * @param dirPath ディレクトリパス
   */
  async listFiles(dirPath: string = ''): Promise<string[]> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    return this.gitFs.readDir(dirPath);
  }

  /**
   * インデックスサイズを取得
   */
  async getIndexSize(): Promise<number> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    return this.vectorStore.size();
  }

  /**
   * コミット履歴を取得
   * @param depth 取得する履歴の深さ
   */
  async getCommitHistory(depth: number = 10): Promise<any[]> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    return this.vectorStore.getCommitHistory(depth);
  }

  /**
   * Gitリポジトリの状態を取得
   */
  async getStatus(): Promise<any[]> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    return this.gitFs.getStatus();
  }

  /**
   * 構造化データをJSONとして保存
   * @param filePath ファイルパス
   * @param data データオブジェクト
   * @param commitMessage コミットメッセージ
   */
  async saveJSON(filePath: string, data: any, commitMessage?: string): Promise<string> {
    const content = JSON.stringify(data, null, 2);
    return this.saveFile(filePath, content, commitMessage);
  }

  /**
   * JSONファイルを読み込み
   * @param filePath ファイルパス
   * @returns パースされたデータ
   */
  async loadJSON(filePath: string): Promise<any> {
    const content = await this.loadFile(filePath);
    return JSON.parse(content);
  }

  /**
   * セッション設定を保存
   * @param config セッション設定
   */
  async saveSessionConfig(config: any): Promise<string> {
    const sessionName = this.getCurrentSession();
    const filePath = `session-configs/${sessionName}.json`;
    
    return this.saveJSON(filePath, {
      sessionName,
      config,
      timestamp: new Date().toISOString(),
    }, `Save session config for ${sessionName}`);
  }

  /**
   * セッション設定を読み込み
   * @param sessionName セッション名（省略時は現在のセッション）
   */
  async loadSessionConfig(sessionName?: string): Promise<any> {
    const session = sessionName || this.getCurrentSession();
    const filePath = `session-configs/${session}.json`;
    
    try {
      return await this.loadJSON(filePath);
    } catch (error) {
      console.warn('Session config not found:', filePath);
      return null;
    }
  }

  /**
   * ベクトルストアを直接取得（高度な操作用）
   */
  getVectorStore(): GitVectorStore {
    return this.vectorStore;
  }

  /**
   * GitFileSystemを直接取得（高度な操作用）
   */
  getGitFileSystem(): GitFileSystem {
    return this.gitFs;
  }

  /**
   * リソースのクリーンアップ
   */
  async cleanup(): Promise<void> {
    try {
      await this.vectorStore.cleanup();
      this.isInitialized = false;
    } catch (error) {
      console.error('Failed to cleanup GitEmbeddingSDK:', error);
      throw error;
    }
  }
}

export default GitEmbeddingSDK; 